import React, { Dispatch, SetStateAction, useEffect } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { legendIconInvalidRequired, legendIconValidRequired } from '../../utils/legend.data.util';
import { removeTooltip, uploadTooltip } from '../../utils/tooltip.util';
import { modalShow } from '../../utils/modal.util';
import { evaluateLegendListObjectRequiredIcon } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { suffixLegend } from '../../scripts/name.legend.script';
import { navBarHide } from '../../utils/nav.bar.util';

import AppLegend from './Legend';
import AppModalVoucherUploadFile from '../modal/ModalVoucherUploadFile';

export interface AppFormVouchersProps {
  record?: any,
  idCompany: string,
  vouchers: {list: boolean, value: {name: string, size: number, type: string, view: string, file: any, id_document_inbox?: string}[], valid: {name: boolean, file: boolean}[]},
  setVouchers: Dispatch<SetStateAction<{list: boolean, value: {name: string, size: number, type: string, view: string, file: any, id_document_inbox?: string}[], valid: {name: boolean, file: boolean}[]}>>,
  setAsideMinimize?: Dispatch<SetStateAction<string>>,
};

const AppFormVouchers: React.FunctionComponent<AppFormVouchersProps> = ({record, idCompany, vouchers, setVouchers, setAsideMinimize}) => {
  const {lang} = StateLanguage()

  const noopAside: Dispatch<SetStateAction<string>> = () => {}
  const asideSetter = setAsideMinimize ?? noopAside

  const handleChangeNameToVoucher = (index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let tempVouchersValue = vouchers.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.name = event.target.value
      }
      return temp_item
    })

    setVouchers({...vouchers, value: tempVouchersValue})
  }

  const validateNameToVoucher = (index: number) => {
    evaluateLegendListObjectRequiredIcon(expressions.text, index, 'name', vouchers, setVouchers, `input-voucher-name${suffixLegend(index)}`, `legend-voucher-name-valid${suffixLegend(index)}`, `legend-voucher-name-required${suffixLegend(index)}`)
  }

  const executeViewVoucher = (index: number) => {
    modalShow(`voucher-pdf-${index}`)
    removeTooltip()
  }

  const executeRemoveVoucher = (index: number) => {
    let tempVouchersValue = vouchers.value.filter((_, temp_index) => (temp_index !== index))
    let tempVouchersValid = vouchers.valid.filter((_, temp_index) => (temp_index !== index))
    setVouchers({...vouchers, value: tempVouchersValue, valid: tempVouchersValid})

    for (let i = 0; i < tempVouchersValue.length; i++) {
      if (expressions) {
        if (expressions.text.test(tempVouchersValue[i].name)) {
          legendIconValidRequired(`input-voucher-name${suffixLegend(i)}`, `legend-voucher-name-valid${suffixLegend(i)}`, `legend-voucher-name-required${suffixLegend(i)}`)
        } else {
          legendIconInvalidRequired(tempVouchersValue[i].name, `input-voucher-name${suffixLegend(i)}`, `legend-voucher-name-valid${suffixLegend(i)}`, `legend-voucher-name-required${suffixLegend(i)}`)
        }
      }
    }

    if (record?.status === 'draft' && tempVouchersValue.length === 0 && tempVouchersValid.length === 0) {
      navBarHide('button-navbar-aside-minimize')
      asideSetter("off")
    }

    removeTooltip()
  }

  const executeShowModalUploadFileVoucher = () => {
    let allow: boolean = true

    for (let i = 0; i < vouchers.valid.length; i++) {
      if (!vouchers.valid[i].name) {
        legendIconInvalidRequired(vouchers.value[i].name, `input-voucher-name${suffixLegend(i)}`, `legend-voucher-name-valid${suffixLegend(i)}`, `legend-voucher-name-required${suffixLegend(i)}`)
        allow = false
      }
    }

    if (allow) {
      modalShow('modal-voucher-upload-file')
    }

    removeTooltip()
  }

  useEffect( () => {
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <div className="card card-flush mb-5">
        <div className="card-header">
          <div className="card-title">
            <h4>{lang.labels.vouchers}</h4>
          </div>
          <div className="card-toolbar">
            <button className="btn btn-sm btn-light-primary" type="button" onClick={executeShowModalUploadFileVoucher}>
              <div className="d-flex align-items-center">
                <span className="svg-icon svg-icon-3 ms-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                    <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                    <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                  </svg>
                </span>
                {lang.labels.addVouchers}
              </div>
            </button>
          </div>
        </div>
        <div className="card-body pt-0">
          <div className="dataTables_wrapper">
            <div className="table-responsive">
              <table className="table align-middle g-1 mb-2">
                <thead className="border-bottom">
                  <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                    <th className="min-w-300px text-nowrap">{lang.labels.file}</th>
                    <th className="w-100px"></th>
                  </tr>
                </thead>
                { vouchers.value.length > 0 && vouchers.valid.length > 0
                  ?
                  <tbody>
                    { vouchers.value.map (( (item, index) => { return (
                      <tr key={index}>
                        <td valign="top">
                          <input id={`input-voucher-name${suffixLegend(index)}`} className="form-control" type="text" autoComplete="off" name="name" value={item.name} onChange={(event) => handleChangeNameToVoucher(index, event)} onKeyUp={() => validateNameToVoucher(index)} onBlur={() => validateNameToVoucher(index)} />
                          <div id={`voucher-pdf${suffixLegend(index)}`} className="modal fade" tabIndex={-1} aria-hidden="true">
                            <div className="modal-dialog modal-dialog-centered">
                              <div className="modal-content">
                                <iframe id='modal-voucher-view-container' title={item.name} src={item.view} width="100%" height="100%" />
                              </div>
                            </div>
                          </div>
                          <AppLegend attribute={{check: vouchers.valid[index].name, name: "voucher-name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                        </td>
                        <td valign="top" className="text-end">
                          <button className="btn btn-icon btn-light-primary" type="button" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="left" data-bs-original-title={lang.labels.viewVoucher} onClick={() => executeViewVoucher(index)}>
                            <span className="svg-icon svg-icon-3">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM15 17C15 16.4 14.6 16 14 16H8C7.4 16 7 16.4 7 17C7 17.6 7.4 18 8 18H14C14.6 18 15 17.6 15 17ZM17 12C17 11.4 16.6 11 16 11H8C7.4 11 7 11.4 7 12C7 12.6 7.4 13 8 13H16C16.6 13 17 12.6 17 12ZM17 7C17 6.4 16.6 6 16 6H8C7.4 6 7 6.4 7 7C7 7.6 7.4 8 8 8H16C16.6 8 17 7.6 17 7Z" fill="black"/>
                                <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black"/>
                              </svg>
                            </span>
                          </button>
                          <button className="btn btn-icon btn-light-primary ms-2" type="button" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="left" data-bs-original-title={lang.labels.removeVoucher} onClick={() => executeRemoveVoucher(index)}>
                            <span className="svg-icon svg-icon-3">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                              </svg>
                            </span>
                          </button>
                        </td>
                      </tr>
                      )}))}
                  </tbody>
                  :
                  <tbody>
                    <tr>
                      <td colSpan={3}>
                        <div className="form-control form-control-sm text-center text-uppercase">{lang.labels.noRecords}</div>
                      </td>
                    </tr>
                  </tbody>
                }
              </table>
            </div>
          </div>
        </div>
      </div>
      <AppModalVoucherUploadFile setAsideMinimize={asideSetter} record={record ?? {}} idCompany={idCompany} vouchers={vouchers} setVouchers={setVouchers}></AppModalVoucherUploadFile>
    </>
  )
};

export default AppFormVouchers;
